/**
 * Checks if an error is a request cancellation error
 * These errors are expected when users navigate away or React Query cancels requests
 * They should NOT be reported to Sentry
 */
export function isCancellationError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  // Check for AbortError (from fetch API)
  if (error instanceof Error && error.name === 'AbortError') {
    return true;
  }

  // Safari/WebKit reports torn-down in-flight requests as TypeError: Load failed
  // instead of AbortError (e.g. during client-side navigation). Do not treat
  // Chromium/Firefox "Failed to fetch" here — that can be a real offline failure.
  if (
    error instanceof TypeError &&
    error.message.toLowerCase().includes('load failed')
  ) {
    return true;
  }

  // Check for axios cancel flag
  if ('__CANCEL__' in error) {
    return true;
  }

  // Check error message for cancellation keywords
  if ('message' in error && typeof error.message === 'string') {
    const message = error.message.toLowerCase();
    if (message.includes('cancel') || message.includes('abort')) {
      return true;
    }
  }

  // Check error code
  if ('code' in error) {
    const code = typeof error.code === 'string' ? error.code : '';
    if (code === 'ERR_CANCELED' || code === 'ECONNABORTED') {
      return true;
    }
  }

  return false;
}
